import React, { useState } from 'react';
import { Button, Text } from '@vibe/core';
import { Info, NavigationChevronLeft, NavigationChevronRight } from '@vibe/icons';

import step1Img from '../../../public/howto-step1-add-tab.png';
import step2Img from '../../../public/howto-step2-installed-apps.png';
import step3Img from '../../../public/howto-step3-desktop-view.png';
import step4Img from '../../../public/howto-step4-mobile-view.png';

const STEPS = [
  {
    title: 'Open any item and click "+"',
    description:
      'Open any monday.com item and click the + button in the item\'s tab bar to add a new view.',
    image: step1Img,
    imageAlt: 'Item tab bar showing the + button to add a new view',
    imageMaxWidth: 340,
  },
  {
    title: 'Find Time Tracking in Installed Apps',
    description:
      'In the Item Views Center, go to Installed Apps and find Time Tracking by WorkHub. Click "Add to item".',
    image: step2Img,
    imageAlt: 'Item Views Center showing Time Tracking app',
    imageMaxWidth: 680,
  },
  {
    title: 'Log time from the item (Desktop)',
    description:
      'The Time Tracking tab shows Original Estimate, Logged, and Remaining hours. Use the Log Time form to record work — pick a date, enter hours, and add an optional description.',
    image: step3Img,
    imageAlt: 'Desktop item view with Time Tracking tab',
    imageMaxWidth: 740,
  },
  {
    title: 'Log time from the item (Mobile)',
    description:
      'The same Time Tracking tab works in the monday.com mobile app. Your team can log hours on the go — on-site, on the road, or between meetings.',
    image: step4Img,
    imageAlt: 'Mobile time tracking view',
    imageMaxWidth: 360,
  },
];

const ItemTimeTrackingGuide: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  const current = STEPS[step];
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;

  const handleOpen = () => {
    setStep(0);
    setOpen(true);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) setOpen(false);
  };

  return (
    <>
      <button
        className="rp-howto-trigger"
        onClick={handleOpen}
        aria-label="How to log work from items"
      >
        <Info size="16" />
        <span>How to log work from items (mobile + desktop)</span>
      </button>

      {open && (
        <div className="rp-howto-overlay" onClick={handleOverlayClick}>
          <div className="rp-howto-dialog">
            {/* Header */}
            <div className="rp-howto-dialog__header">
              <Text type={Text.types.TEXT1} weight={Text.weights.BOLD}>
                How to log work from items (mobile + desktop)
              </Text>
              <button
                className="rp-howto-dialog__close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            {/* Step indicator */}
            <div className="rp-howto-dialog__steps-indicator">
              {STEPS.map((_, i) => (
                <button
                  key={i}
                  className={`rp-howto-dialog__dot${i === step ? ' rp-howto-dialog__dot--active' : ''}`}
                  onClick={() => setStep(i)}
                  aria-label={`Step ${i + 1}`}
                />
              ))}
            </div>

            {/* Content */}
            <div className="rp-howto-dialog__body">
              <div className="rp-howto-dialog__step-number">
                Step {step + 1} of {STEPS.length}
              </div>
              <p className="rp-howto-dialog__title">{current.title}</p>
              <p className="rp-howto-dialog__description">{current.description}</p>
              <div className="rp-howto-dialog__image-wrapper">
                <img
                  src={current.image}
                  alt={current.imageAlt}
                  style={{ maxWidth: current.imageMaxWidth }}
                  className="rp-howto-dialog__image"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="rp-howto-dialog__footer">
              <Button
                kind={Button.kinds.TERTIARY}
                size={Button.sizes.SMALL}
                leftIcon={NavigationChevronLeft}
                onClick={() => setStep((s) => s - 1)}
                disabled={isFirst}
              >
                Back
              </Button>
              <div className="rp-howto-dialog__footer-right">
                {isLast ? (
                  <Button
                    size={Button.sizes.SMALL}
                    onClick={() => setOpen(false)}
                  >
                    Got it
                  </Button>
                ) : (
                  <Button
                    size={Button.sizes.SMALL}
                    rightIcon={NavigationChevronRight}
                    onClick={() => setStep((s) => s + 1)}
                  >
                    Next
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ItemTimeTrackingGuide;
